<!-- DeliveryOverview component -->
<template>
	<div class="delivery-overview" v-if="showDashboard">
		<b-card no-body class="delivery-overview-card">
			<div class="grid-wrapper">
				<div class="header-wrapper">
					<div class="header">
						<h4 class="font-weight-bold title">
							{{ $t('PORTAL.delivery_overview.title') }}
						</h4>
						<div
							class="font-weight-normal subtitles"
							v-if="lastUpdateTime"
						>
							{{
								$t(
									`PORTAL.Deliveries.TIMELINES.LAST_UPDATE_TIME`,
									{
										lastUpdateTime
									}
								)
							}}
						</div>
						<div class="refresh-spinner">
							<span
								:class="`${
									isFetching
										? 'spinner-border spinner-border-sm'
										: 'mdi mdi-refresh'
								}`"
								@click="fetchOverview"
								role="status"
								aria-hidden="true"
							/>
						</div>
					</div>
				</div>
				<div class="badges-wrapper">
					<div class="badges">
						<div v-for="status in STATUSES" :key="status.name">
							<b-badge :class="`${status.class} badge`">
								<span>
									{{
										$t(
											`PORTAL.Deliveries.STATUS.${status.name}`
										)
									}}
								</span>
							</b-badge>
							<h5>
								{{
									isFetching ||
									statusCount[status.name] === undefined
										? '-'
										: statusCount[status.name]
								}}
							</h5>
						</div>
					</div>
				</div>
				<div class="filter-wrapper">
					<div class="filters">
						<div class="filter">
							<div class="font-weight-bold">
								{{
									$t(
										'PORTAL.Reports.filter_report_date_label'
									)
								}}
							</div>
							<b-input-group class="date-picker-container">
								<b-input-group-append>
									<b-form-datepicker
										v-model="filter.date"
										button-only
										@input="fetchOverview"
										:min="MIN_DATE"
										:max="MAX_DATE"
									/>
								</b-input-group-append>
								<b-form-input
									v-model="filter.date"
									type="text"
									readonly
								/>
							</b-input-group>
						</div>
						<div class="filter">
							<div class="font-weight-bold">
								{{ $t('PORTAL.Reports.filter_client_label') }}
							</div>
							<b-form-select
								v-model="filter.clientId"
								@input="fetchOverview"
								:options="clients.map(client => client.id)"
							></b-form-select>
						</div>
					</div>
				</div>
			</div>
		</b-card>
		<b-toast
			v-model="showError"
			@hidden="dismissError"
			title="Error"
			id="dashboard-error-toast"
			toaster="b-toaster-top-center"
		>
			{{ errorMsg }}
		</b-toast>
	</div>
</template>

<script>
import {
	dashboardComputed,
	configComputed,
	dashboardMethods
} from '@/store/helpers'

import { DELIVERY_STATUS } from '@/utils/constants'

const STATUSES = [
	{
		name: DELIVERY_STATUS.AWAITS_DRIVER,
		class: 'badge-secondary'
	},
	{
		name: DELIVERY_STATUS.AWAITS_PICKUP,
		class: 'badge-secondary'
	},
	{
		name: DELIVERY_STATUS.FAILED_PICKUP,
		class: 'badge-secondary'
	},
	{
		name: DELIVERY_STATUS.DELIVERING,
		class: 'badge-secondary'
	},
	{
		name: DELIVERY_STATUS.FAILED,
		class: 'badge-secondary'
	},
	{
		name: DELIVERY_STATUS.RESCHEDULED,
		class: 'badge-secondary'
	},
	{
		name: DELIVERY_STATUS.DELIVERED,
		class: 'badge-success'
	},
	{
		name: DELIVERY_STATUS.RETURNED,
		class: 'badge-warning'
	},
	{
		name: DELIVERY_STATUS.CANCELED,
		class: 'badge-warning'
	}
]

const PAST_DAYS = 59 // 60 days with today inclusive
const MAX_DATE = new Date()
const MIN_DATE = new Date()
MIN_DATE.setDate(MIN_DATE.getDate() - PAST_DAYS)

const TODAY_STR = MAX_DATE.toISOString().slice(0, 10)

export default {
	name: 'DeliveryOverview',
	data() {
		return {
			STATUSES,
			MAX_DATE,
			MIN_DATE
		}
	},
	computed: { ...dashboardComputed, ...configComputed },
	methods: {
		...dashboardMethods
	},
	watch: {
		['filter.date'](newDate, prevDate) {
			if (newDate === TODAY_STR) {
				this.setPolling()
			} else if (prevDate === TODAY_STR) {
				this.clearPolling()
			}
		},
		showDashboard(isOpen) {
			if (isOpen) {
				if (this.filter.date === TODAY_STR) {
					this.setPolling()
				}
			} else {
				this.clearPolling()
			}
		}
	},
	mounted() {
		if (
			this.showDashboard &&
			this.filter.date === TODAY_STR &&
			this.polling === null
		) {
			this.setPolling()
		}
	}
}
</script>

<style scoped lang="scss">
.grid-wrapper {
	display: grid;

	.header-wrapper {
		grid-column: 1 / 2;
		grid-row: 1;
	}
	.badges-wrapper {
		grid-column: 1 / 2;
		grid-row: 2;

		display: flex;
		overflow: auto;
	}
	.filter-wrapper {
		grid-column: 2;
		grid-row: 1 / 3;

		display: flex;
		justify-content: flex-end;
		padding-left: 4px;
	}
}
.delivery-overview-card {
	margin-top: 8px;
	padding: 8px;
}
.left-container {
	display: flex;
	flex-direction: column;
}
.header {
	display: flex;
	align-items: center;
	gap: 12px;
}
.refresh-spinner {
	padding-bottom: 5px;

	.mdi-refresh {
		display: inline-block;
		height: 16px;
		font-size: 24px;
		color: #98a6ad;
		margin-left: -4px;

		&:hover {
			color: #6c757d;
			cursor: pointer;
		}
	}
}
.badges {
	display: grid;
	grid-template-columns: repeat(9, 1fr);
	grid-gap: 4px;

	> div {
		display: flex;
		flex-direction: column;
		background: #f4f4f4;
		border-radius: 4px;
		align-items: center;
	}

	.badge {
		width: 100%;
	}
}
.filters {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 8px;
	min-width: 125px;

	.filter {
		display: flex;
		align-items: center;
		gap: 8px;

		> div {
			white-space: nowrap;
		}
	}
}
.custom-select,
.date-picker-container input {
	background-color: #eef2f7;
	color: #313a46;
}

.date-picker-container {
	input {
		border-left: none;
		padding: 0.45rem 0.2rem;
	}
	&::v-deep .dropdown-toggle {
		border-top-left-radius: 0.25rem !important;
		border-bottom-left-radius: 0.25rem !important;
		padding: 0.45rem 0.5rem;
		background-color: #eef2f7;
		color: #6c757d;
		border: 1px solid #dee2e6;
		border-right: none;
		box-shadow: none;
	}
}
</style>
